import { Channel } from './Channel';
import { DynamicFlatNode } from './DynamicFlatNode.class';
import { TreeLevel } from './enum';
import { TreeEntityType } from './enum/TreeEntityType';
import type { IMeasurementConfigurationModel } from './interfaces/measurement-configuration-model.interface';

/**
 * Represents the configuration model for a measurement.
 * Implements the IMeasurementConfigurationModel interface.
 */
export class MeasurementConfigurationModel implements IMeasurementConfigurationModel {
  /** List of channels associated with the measurement configuration. */
  channels: Channel[] = [];

  /** Unique identifier for the measurement configuration. */
  identifier!: string;

  /** Identifier of the parent configuration. */
  parentIdentifier!: string;

  /** Name of the measurement configuration. */
  name!: string;

  /** Description of the measurement configuration. */
  description!: string;

  /** URL associated with the measurement configuration. */
  url!: string;

  /**
   * Constructs a new instance of MeasurementConfigurationModel.
   * @param args Partial object to initialize the model properties.
   */
  constructor(args: Partial<MeasurementConfigurationModel>) {
    if (args) {
      Object.assign(this, args);
    }
    if (this.channels) {
      this.channels = this.channels.map((c) => new Channel(c));
    }
  }

  /**
   * Returns the entity representation of the measurement configuration.
   */
  get entity(): IMeasurementConfigurationModel {
    return {
      channels: this.channels,
      identifier: this.identifier,
      parentIdentifier: this.parentIdentifier,
      name: this.name,
      description: this.description,
      url: this.url,
    };
  }

  /**
   * Converts the measurement configuration to a DynamicFlatNode.
   * @returns A DynamicFlatNode instance representing the configuration.
   */
  toDynamicFlatNode() {
    return new DynamicFlatNode({
      label: this.name,
      id: this.identifier,
      isLoading: false,
      level: TreeLevel.Group,
      expandable: true,
      entity: this.entity,
      type: TreeEntityType.CHANNEL_GROUP,
    });
  }
}
